import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Copy, Trash2, Pencil, AlertTriangle, Clock, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SHIFT_TYPES = [
  { value: 'regular', label: 'Regular' },
  { value: 'on_call', label: 'On-Call' },
];

const DuplicateShiftModal = ({ duplicate, open, onClose, onResolved }) => {
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  if (!duplicate) return null;

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleDelete = async (shift) => {
    setActionLoading(shift.id);
    try {
      await axios.delete(`${API}/schedules/${shift.id}`, { headers });
      toast.success(`Deleted ${shift.shift_type} shift (${shift.start_time}-${shift.end_time})`);
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete shift');
    } finally {
      setActionLoading(null);
    }
  };

  const startEdit = (shift) => {
    setEditingShiftId(shift.id);
    setEditForm({
      start_time: shift.start_time,
      end_time: shift.end_time,
      shift_type: shift.shift_type,
    });
  };

  const cancelEdit = () => {
    setEditingShiftId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (shift) => {
    setActionLoading(shift.id);
    try {
      await axios.put(`${API}/schedules/${shift.id}`, {
        user_id: duplicate.user_id,
        date: duplicate.date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        shift_type: editForm.shift_type,
        role: shift.role || duplicate.role,
      }, { headers });
      toast.success('Shift updated successfully');
      setEditingShiftId(null);
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update shift');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="duplicate-shift-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Copy className="h-5 w-5 text-red-500" />
            <span>Resolve Duplicate Shifts</span>
          </DialogTitle>
          <DialogDescription>
            {duplicate.name} has {duplicate.shift_count} shifts on {formatDate(duplicate.date)}
          </DialogDescription>
        </DialogHeader>

        {/* Employee info bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900">{duplicate.name}</span>
            <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-600 text-xs capitalize">{duplicate.role?.replace('_', ' ')}</span>
          </div>
          {duplicate.has_overlap && (
            <span className="flex items-center space-x-1 text-xs text-red-600 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Time Overlap</span>
            </span>
          )}
        </div>

        {/* Shifts list */}
        <div className="space-y-3 mt-1">
          {duplicate.shifts?.map((shift, idx) => {
            const isEditing = editingShiftId === shift.id;
            const isLoading = actionLoading === shift.id;
            const isRecommendedRemove = duplicate.recommended_remove_id === shift.id;

            return (
              <div
                key={shift.id || idx}
                className={`border rounded-lg overflow-hidden transition-colors ${
                  isEditing ? 'border-blue-300 bg-blue-50/30' : isRecommendedRemove ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'
                }`}
                data-testid={`modal-shift-${shift.id || idx}`}
              >
                {/* Recommended removal banner */}
                {isRecommendedRemove && !isEditing && (
                  <div className="px-4 py-1.5 bg-red-50 border-b border-red-100 flex items-center space-x-2" data-testid="recommended-remove-banner">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-xs font-medium text-red-700">Recommended to remove</span>
                    {duplicate.recommended_reason && (
                      <span className="text-xs text-red-500">— {duplicate.recommended_reason}</span>
                    )}
                  </div>
                )}
                {/* PTO protected indicator */}
                {shift.shift_type === 'pto' && !isRecommendedRemove && !isEditing && (
                  <div className="px-4 py-1.5 bg-green-50 border-b border-green-100 flex items-center space-x-2" data-testid="pto-protected-banner">
                    <span className="text-xs font-medium text-green-700">Protected — PTO takes priority over other shifts</span>
                  </div>
                )}
                {/* Shift header */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-8 rounded-full ${
                      shift.shift_type === 'on_call' ? 'bg-orange-400' : shift.shift_type === 'pto' ? 'bg-green-400' : 'bg-blue-400'
                    }`} />
                    {isEditing ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={editForm.shift_type}
                          onChange={(e) => setEditForm(f => ({ ...f, shift_type: e.target.value }))}
                          className="text-sm border rounded px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                          data-testid="edit-shift-type"
                        >
                          {SHIFT_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        <Input
                          type="time"
                          value={editForm.start_time}
                          onChange={(e) => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                          className="w-28 text-sm"
                          data-testid="edit-start-time"
                        />
                        <span className="text-gray-400">-</span>
                        <Input
                          type="time"
                          value={editForm.end_time}
                          onChange={(e) => setEditForm(f => ({ ...f, end_time: e.target.value }))}
                          className="w-28 text-sm"
                          data-testid="edit-end-time"
                        />
                      </div>
                    ) : (
                      <div>
                        <span className={`text-sm font-semibold capitalize ${
                          shift.shift_type === 'on_call' ? 'text-orange-700' : 'text-blue-700'
                        }`}>
                          {shift.shift_type?.replace('_', ' ')}
                        </span>
                        <div className="flex items-center space-x-1 text-sm text-gray-600 mt-0.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{shift.start_time} - {shift.end_time}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center space-x-1.5">
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEdit}
                          disabled={isLoading}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                          data-testid="cancel-edit-btn"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSaveEdit(shift)}
                          disabled={isLoading}
                          className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="save-edit-btn"
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(shift)}
                          disabled={!!actionLoading}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`edit-shift-${shift.id || idx}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(shift)}
                          disabled={!!actionLoading}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-shift-${shift.id || idx}`}
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-2">
          <Button variant="outline" onClick={onClose} data-testid="close-duplicate-modal">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateShiftModal;
